import { useState } from 'react';
import { Plus, RefreshCw, AlertOctagon } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useCarburantDashboard, useBonsCarburant, useMines, useCamions, useCreateDepot } from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { SaisieBonCarburantDialog } from '@/components/operations/transport/SaisieBonCarburantDialog';
import { AnomalieDetailDialog } from '@/components/operations/transport/AnomalieDetailDialog';
import type { AnomalieCarburant } from '@/types';
import {
  RATIO_GRAVITE_COLORS, RATIO_GRAVITE_ICONS, RATIO_GRAVITE_LABELS,
  STATUT_ANOMALIE_LABELS, type RatioGraviteCarburant,
} from '@/types';
import { can } from '@/utils/permissions';

const fmtInt = (n: number, dec = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: dec, minimumFractionDigits: dec })
    .format(n)
    .replace(/[  ]/g, ' ');

const fmtHeure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Page Carburant — Brief SG-RLT-CDC-MODULES-145 §5.2 + §5.3 + §5.4.
 * Tabs : Dashboard · Bons · Anomalies.
 */
export const CarburantPage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const [tab, setTab] = useState<'dashboard' | 'bons' | 'anomalies' | 'depots' | 'rapport'>('dashboard');
  const [dialogOpen, setDialogOpen] = useState(false);

  const canSaisir = can(role, 'operations.manage');

  return (
    <div className="ds-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h1 style={{ fontFamily: 'var(--font-t)', fontSize: 22, fontWeight: 700, color: 'var(--p-deep)', margin: 0, flex: 1 }}>
          Carburant
        </h1>
        {canSaisir && (
          <button type="button" onClick={() => setDialogOpen(true)} className="ds-btn ds-btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Saisir un bon
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--bdr)', marginBottom: 14 }}>
        {[
          { id: 'dashboard',  label: 'Tableau de bord' },
          { id: 'bons',       label: 'Bons carburant' },
          { id: 'anomalies',  label: 'Anomalies' },
          { id: 'depots',     label: 'Dépôts' },
          { id: 'rapport',    label: 'Rapport mensuel' },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id as 'dashboard' | 'bons' | 'anomalies' | 'depots' | 'rapport')} style={{
            padding: '8px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            color: tab === t.id ? 'var(--p)' : 'var(--tx-3)',
            borderBottom: tab === t.id ? '2px solid var(--p)' : '2px solid transparent',
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard'  && <DashboardTab />}
      {tab === 'bons'       && <BonsTab />}
      {tab === 'anomalies'  && <AnomaliesTab />}
      {tab === 'depots'     && <DepotsTab />}
      {tab === 'rapport'    && <RapportMensuelTab />}

      <SaisieBonCarburantDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
};

// ── Onglet Dashboard ────────────────────────────────────────────────────────

const DashboardTab = () => {
  const { data, isLoading, refetch, dataUpdatedAt } = useCarburantDashboard();
  if (isLoading || !data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>;

  return (
    <>
      {/* Alertes */}
      {data.alertes.length > 0 && (
        <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 14, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          {data.alertes.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ fontSize: 16 }}>{a.icone}</span>
              <strong style={{ flex: 1 }}>{a.texte}</strong>
            </div>
          ))}
        </div>
      )}

      {/* 4 KPI */}
      <div className="ds-kpi-row" style={{ marginBottom: 14, gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="ds-kr" style={{ borderLeft: '4px solid #3b82f6' }}>
          <div className="ds-kr-ico">📋</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(data.kpis.bonsMois)}</div>
          <div className="ds-kr-lbl">Bons saisis (mois)</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: '4px solid #a855f7' }}>
          <div className="ds-kr-ico">⛽</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(data.kpis.litresMois)} L</div>
          <div className="ds-kr-lbl">Litres distribués (mois)</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="ds-kr-ico">⚠️</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums', color: data.kpis.anomaliesMois > 0 ? '#dc2626' : undefined }}>{data.kpis.anomaliesMois}</div>
          <div className="ds-kr-lbl">Anomalies (mois)</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="ds-kr-ico">🚛</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {data.kpis.consoMoyL100kmFlotte !== null ? `${data.kpis.consoMoyL100kmFlotte.toFixed(1)}` : '—'}
            <span style={{ fontSize: 13, color: 'var(--tx-3)' }}> L/100km</span>
          </div>
          <div className="ds-kr-lbl">Conso moy. flotte</div>
        </div>
      </div>

      {/* Graphique consommation 30j + Répartition par dépôt */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 14 }}>
        <div className="ds-bp">
          <div className="ds-bp-h">
            <div>
              <div className="ds-bp-t">Évolution consommation (30 jours)</div>
              <div className="ds-bp-s">Litres distribués / jour</div>
            </div>
          </div>
          <div className="ds-bp-b" style={{ padding: 12, height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.evolution30j} margin={{ top: 5, right: 8, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => fmtDate(d)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={(d) => typeof d === 'string' ? fmtDate(d) : ''} formatter={(v) => [`${fmtInt(Number(v))} L`, 'Distribués']} />
                <Area type="monotone" dataKey="litres" stroke="#a855f7" fill="#a855f7" fillOpacity={0.2} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="ds-bp">
          <div className="ds-bp-h">
            <div>
              <div className="ds-bp-t">Par dépôt (mois)</div>
              <div className="ds-bp-s">Litres distribués / bons</div>
            </div>
          </div>
          <div className="ds-bp-b">
            {data.parDepot.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>Aucune mine</div>
            ) : (
              <table className="ds-tbl">
                <thead><tr><th>Dépôt</th><th className="ds-am">Litres</th><th className="ds-am">Bons</th></tr></thead>
                <tbody>
                  {data.parDepot.map((d) => (
                    <tr key={d.code}>
                      <td>
                        <span className="ds-ref-code">{d.code}</span>
                        <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{d.nom}</div>
                      </td>
                      <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)' }}>{fmtInt(d.litres)} L</td>
                      <td className="ds-am">{d.bons}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Anomalies en cours */}
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t">Anomalies en cours</div>
            <div className="ds-bp-s">{data.anomaliesOuvertes.length} anomalie(s) ouverte/en investigation</div>
          </div>
          <AlertOctagon size={16} style={{ color: '#dc2626' }} />
        </div>
        <div className="ds-bp-b">
          {data.anomaliesOuvertes.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#16a34a', fontWeight: 600, fontSize: 13 }}>
              ✅ Aucune anomalie en cours
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>N° bon</th>
                  <th>Camion</th>
                  <th>Chauffeur</th>
                  <th>Mine</th>
                  <th className="ds-am">Ratio</th>
                  <th>Gravité</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.anomaliesOuvertes.map((a) => (
                  <tr key={a.id} style={{ background: a.estRecidive ? 'rgba(239, 68, 68, 0.08)' : undefined }}>
                    <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{fmtDate(a.createdAt)} {fmtHeure(a.createdAt)}</td>
                    <td><span className="ds-ref-code">{a.bonNumero}</span></td>
                    <td style={{ fontWeight: 600 }}>{a.camionCode}</td>
                    <td style={{ fontSize: 12 }}>{a.chauffeur}</td>
                    <td>{a.mineCode}</td>
                    <td className="ds-am" style={{ fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{a.ratioPct.toFixed(0)}%</td>
                    <td style={{ fontSize: 11, textTransform: 'uppercase', color: a.gravite === 'grave' ? '#dc2626' : '#f59e0b' }}>
                      {a.gravite}
                      {a.estRecidive && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#dc2626', color: 'white', borderRadius: 4, fontSize: 9 }}>RÉCIDIVE</span>}
                    </td>
                    <td style={{ fontSize: 11 }}>{STATUT_ANOMALIE_LABELS[a.statut as keyof typeof STATUT_ANOMALIE_LABELS] ?? a.statut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ padding: 10, borderRadius: 'var(--r-sm)', border: '1px dashed var(--bdr)', background: 'var(--surface)', textAlign: 'center', fontSize: 11, color: 'var(--tx-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        ● <strong>LIVE</strong> · Actualisé {new Date(dataUpdatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        <button type="button" onClick={() => refetch()} style={{
          padding: '2px 10px', border: '1px solid var(--bdr)', background: 'transparent',
          borderRadius: 4, cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshCw size={11} /> Actualiser
        </button>
      </div>
    </>
  );
};

// ── Onglet Bons ─────────────────────────────────────────────────────────────

const BonsTab = () => {
  const [date, setDate]                 = useState(todayIso());
  const [mineId, setMineId]             = useState('');
  const [camionId, setCamionId]         = useState('');
  const [gravite, setGravite]           = useState<RatioGraviteCarburant | ''>('');
  const [anomalieOnly, setAnomalieOnly] = useState(false);

  const { data, isLoading } = useBonsCarburant({
    date,
    mineId:   mineId   || undefined,
    camionId: camionId || undefined,
    gravite:  gravite  || undefined,
    anomalieSeulement: anomalieOnly,
    limit: 200,
  });
  const { data: minesData }   = useMines({ actif: true, limit: 100 });
  const { data: camionsData } = useCamions({ actif: true, limit: 500 });
  const bons    = data?.data ?? [];
  const mines   = minesData?.data   ?? [];
  const camions = camionsData?.data ?? [];

  return (
    <>
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-b" style={{ padding: 12 }}>
          <div className="grid grid-cols-5 gap-3" style={{ alignItems: 'end' }}>
            <Field label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Dépôt">
              <select value={mineId} onChange={(e) => setMineId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {mines.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.nom}</option>)}
              </select>
            </Field>
            <Field label="Camion">
              <select value={camionId} onChange={(e) => setCamionId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {camions.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </Field>
            <Field label="Gravité">
              <select value={gravite} onChange={(e) => setGravite(e.target.value as RatioGraviteCarburant | '')} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Toutes —</option>
                {(Object.keys(RATIO_GRAVITE_LABELS) as RatioGraviteCarburant[]).map((g) => (
                  <option key={g} value={g}>{RATIO_GRAVITE_ICONS[g]} {RATIO_GRAVITE_LABELS[g]}</option>
                ))}
              </select>
            </Field>
            <Field label="Filtre rapide">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6 }}>
                <input type="checkbox" checked={anomalieOnly} onChange={(e) => setAnomalieOnly(e.target.checked)} />
                Anomalies seulement
              </label>
            </Field>
          </div>
        </div>
      </div>

      <div className="ds-bp">
        <div className="ds-bp-b">
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
          ) : bons.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Aucun bon pour ces filtres</div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>N° bon</th>
                  <th>Date/heure</th>
                  <th>Camion</th>
                  <th>Chauffeur</th>
                  <th>Dépôt</th>
                  <th className="ds-am">Km</th>
                  <th className="ds-am">Servi (L)</th>
                  <th className="ds-am">Théo (L)</th>
                  <th className="ds-am">Ratio</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {bons.map((b) => {
                  const g = b.gravite ?? 'normal';
                  return (
                    <tr key={b.id}>
                      <td><span className="ds-ref-code">{b.numero}</span></td>
                      <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>
                        {fmtDate(b.dateHeure)} {fmtHeure(b.dateHeure)}
                      </td>
                      <td style={{ fontWeight: 600 }}>{b.camion?.code ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{b.chauffeurNom}</td>
                      <td>{b.mine?.code ?? '—'}</td>
                      <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(b.kmCompteur)}</td>
                      <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)', fontVariantNumeric: 'tabular-nums' }}>{parseFloat(b.qteServieL).toFixed(1)}</td>
                      <td className="ds-am" style={{ color: 'var(--tx-3)', fontVariantNumeric: 'tabular-nums' }}>{b.consoTheoriqueL ? parseFloat(b.consoTheoriqueL).toFixed(1) : '—'}</td>
                      <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: RATIO_GRAVITE_COLORS[g] }}>
                        {b.ratioPct ? `${parseFloat(b.ratioPct).toFixed(0)}%` : '—'} {b.gravite ? RATIO_GRAVITE_ICONS[b.gravite] : ''}
                      </td>
                      <td style={{ fontSize: 11 }}>
                        {b.anomalie && (
                          <span style={{ padding: '2px 6px', background: '#dc2626', color: 'white', borderRadius: 4, fontSize: 9, fontWeight: 700 }}>
                            ANOMALIE{b.anomalie.estRecidive ? ' RÉCIDIVE' : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

// ── Onglet Anomalies ────────────────────────────────────────────────────────

import { useAnomaliesCarburant, useCreateMouvement, useDepots, useMouvementsDepot } from '@/hooks/useTransport';
const AnomaliesTab = () => {
  const { data, isLoading } = useAnomaliesCarburant({ limit: 200 });
  const anomalies = data?.data ?? [];
  const [selected, setSelected] = useState<AnomalieCarburant | null>(null);

  return (
    <div className="ds-bp">
      <div className="ds-bp-h">
        <div>
          <div className="ds-bp-t">Toutes les anomalies</div>
          <div className="ds-bp-s">{anomalies.length} anomalies (clic ligne → workflow investigation)</div>
        </div>
      </div>
      <div className="ds-bp-b">
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>Chargement…</div>
        ) : anomalies.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#16a34a' }}>✅ Aucune anomalie enregistrée</div>
        ) : (
          <table className="ds-tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>N° bon</th>
                <th>Camion</th>
                <th>Mine</th>
                <th className="ds-am">Ratio</th>
                <th>Gravité</th>
                <th>Récidive</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.map((a) => (
                <tr key={a.id} onClick={() => setSelected(a)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{fmtDate(a.createdAt)}</td>
                  <td><span className="ds-ref-code">{a.bonCarburant?.numero ?? '—'}</span></td>
                  <td style={{ fontWeight: 600 }}>{a.bonCarburant?.camion?.code ?? '—'}</td>
                  <td>{a.bonCarburant?.mine?.code ?? '—'}</td>
                  <td className="ds-am" style={{ fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{parseFloat(a.ratioConstate).toFixed(0)}%</td>
                  <td style={{ fontSize: 11, textTransform: 'uppercase', color: a.gravite === 'grave' ? '#dc2626' : a.gravite === 'modere' ? '#f59e0b' : '#facc15' }}>
                    {a.gravite}
                  </td>
                  <td>{a.estRecidive ? <span style={{ padding: '2px 6px', background: '#dc2626', color: 'white', borderRadius: 4, fontSize: 9, fontWeight: 700 }}>OUI</span> : '—'}</td>
                  <td style={{ fontSize: 11 }}>{STATUT_ANOMALIE_LABELS[a.statut]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <AnomalieDetailDialog open={!!selected} onClose={() => setSelected(null)} anomalie={selected} />
    </div>
  );
};

// ── Onglet Dépôts ───────────────────────────────────────────────────────────

const DepotsTab = () => {
  const { data: depots = [], isLoading } = useDepots();
  const { data: mouvData }    = useMouvementsDepot({ limit: 50 });
  const { data: minesData }   = useMines({ actif: true, limit: 100 });
  const createDepot           = useCreateDepot();
  const createMouv            = useCreateMouvement();
  const mines                 = minesData?.data ?? [];
  const mouvements            = mouvData?.data ?? [];

  const [appro, setAppro]   = useState<{ open: boolean; depotId?: string }>({ open: false });
  const [newDepot, setNewDepot] = useState<{ open: boolean }>({ open: false });
  const [mineId, setMineId]                 = useState('');
  const [capacite, setCapacite]             = useState('');
  const [responsable, setResponsable]       = useState('');

  const [volume, setVolume]   = useState('');
  const [dateEffet, setDateEffet] = useState(new Date().toISOString().slice(0, 16));
  const [source, setSource]   = useState('');

  const onCreateDepot = async () => {
    if (!mineId) return;
    await createDepot.mutateAsync({
      mineId,
      capaciteTotaleL: capacite ? parseFloat(capacite) : undefined,
      responsableNom:  responsable.trim() || undefined,
    });
    setNewDepot({ open: false }); setMineId(''); setCapacite(''); setResponsable('');
  };

  const onCreateAppro = async () => {
    if (!appro.depotId || !volume) return;
    await createMouv.mutateAsync({
      depotId:   appro.depotId,
      type:      'approvisionnement',
      volumeL:   parseFloat(volume),
      dateEffet: new Date(dateEffet).toISOString(),
      source:    source.trim() || undefined,
    });
    setAppro({ open: false }); setVolume(''); setSource('');
  };

  const minesSansDepot = mines.filter((m) => !depots.some((d) => d.mineId === m.id));

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--tx-2)' }}>Dépôts de carburant (1 par mine)</h3>
        {minesSansDepot.length > 0 && (
          <button type="button" onClick={() => setNewDepot({ open: true })} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Configurer un dépôt
          </button>
        )}
      </div>

      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-b">
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>Chargement…</div>
          ) : depots.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>
              Aucun dépôt configuré. Clique sur « Configurer un dépôt » pour démarrer.
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Mine / Dépôt</th>
                  <th>Responsable</th>
                  <th className="ds-am">Capacité</th>
                  <th className="ds-am">Niveau estimé</th>
                  <th className="ds-am">Conso/jour</th>
                  <th className="ds-am">Autonomie</th>
                  <th className="ds-am">Actions</th>
                </tr>
              </thead>
              <tbody>
                {depots.map((d) => (
                  <tr key={d.id} style={d.alerteNiveauBas ? { background: 'rgba(239,68,68,0.08)' } : undefined}>
                    <td>
                      <strong className="ds-ref-code">{d.mineCode}</strong>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{d.mineNom}</div>
                    </td>
                    <td style={{ fontSize: 12 }}>{d.responsableNom ?? '—'}</td>
                    <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.capaciteTotaleL ? `${fmtInt(d.capaciteTotaleL)} L` : '—'}</td>
                    <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(d.niveauEstimeL)} L</td>
                    <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.consoMoyJourL > 0 ? `${fmtInt(d.consoMoyJourL)} L` : '—'}</td>
                    <td className="ds-am" style={{ fontWeight: 700, color: d.alerteNiveauBas ? '#dc2626' : '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                      {d.autonomieJours !== null ? `${d.autonomieJours.toFixed(1)} j` : '—'}
                      {d.alerteNiveauBas && ' 🔴'}
                    </td>
                    <td className="ds-am">
                      <button type="button" className="ds-btn ds-btn-xs ds-btn-p" onClick={() => setAppro({ open: true, depotId: d.id })}>+ Appro</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Mouvements récents */}
      <div className="ds-bp">
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t">Mouvements récents</div>
            <div className="ds-bp-s">50 derniers (approvisionnements / consommations / ajustements)</div>
          </div>
        </div>
        <div className="ds-bp-b">
          {mouvements.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>Aucun mouvement</div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Dépôt</th>
                  <th>Type</th>
                  <th className="ds-am">Volume</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {mouvements.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{fmtDate(m.dateEffet)} {fmtHeure(m.dateEffet)}</td>
                    <td>{m.depot?.mine.code ?? '—'}</td>
                    <td style={{ fontSize: 11, textTransform: 'uppercase',
                      color: m.type === 'approvisionnement' ? '#16a34a' : m.type === 'consommation' ? '#a855f7' : '#f59e0b' }}>
                      {m.type === 'approvisionnement' ? '↑ Appro' : m.type === 'consommation' ? '↓ Conso' : '⚙ Ajust'}
                    </td>
                    <td className="ds-am" style={{ fontWeight: 700, color: m.type === 'consommation' ? '#dc2626' : '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                      {m.type === 'consommation' ? '−' : '+'} {fmtInt(parseFloat(m.volumeL))} L
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{m.source ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Dialog création dépôt */}
      {newDepot.open && (
        <div style={modalBackdrop} onClick={() => setNewDepot({ open: false })}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Configurer un nouveau dépôt</h3>
            <div className="space-y-2">
              <Field label="Mine *">
                <select value={mineId} onChange={(e) => setMineId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                  <option value="">— Sélectionner —</option>
                  {minesSansDepot.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.nom}</option>)}
                </select>
              </Field>
              <Field label="Capacité totale (L)">
                <input type="number" value={capacite} onChange={(e) => setCapacite(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="50000" />
              </Field>
              <Field label="Responsable">
                <input value={responsable} onChange={(e) => setResponsable(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" className="ds-btn ds-btn-g" onClick={() => setNewDepot({ open: false })}>Annuler</button>
              <button type="button" className="ds-btn ds-btn-p" onClick={onCreateDepot} disabled={!mineId || createDepot.isPending}>Créer</button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog approvisionnement */}
      {appro.open && (
        <div style={modalBackdrop} onClick={() => setAppro({ open: false })}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Enregistrer un approvisionnement</h3>
            <div className="space-y-2">
              <Field label="Volume livré (L) *">
                <input type="number" min={0} step="0.1" value={volume} onChange={(e) => setVolume(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
              <Field label="Date / heure *">
                <input type="datetime-local" value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
              <Field label="Source / origine">
                <input value={source} onChange={(e) => setSource(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Ex : Livraison Nordgold camion-citerne" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" className="ds-btn ds-btn-g" onClick={() => setAppro({ open: false })}>Annuler</button>
              <button type="button" className="ds-btn ds-btn-p" onClick={onCreateAppro} disabled={!volume || createMouv.isPending}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 100,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalCard: React.CSSProperties = {
  background: 'white', borderRadius: 'var(--r-md)', padding: 20,
  width: '90%', maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,.2)',
};

// ── Onglet Rapport mensuel ──────────────────────────────────────────────────

import { useRapportMensuelCarburant } from '@/hooks/useTransport';
import { generateRapportMensuelCarburantPDF } from '@/utils/rapport-mensuel-carburant-pdf';
import { FileDown } from 'lucide-react';

const RapportMensuelTab = () => {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useRapportMensuelCarburant(year, month);

  const onExport = async () => {
    if (!data) return;
    try {
      setExporting(true);
      await generateRapportMensuelCarburantPDF(data);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <Field label="Année">
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="ds-fi" style={{ width: 100 }} />
        </Field>
        <Field label="Mois">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="ds-fi" style={{ width: 150 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long' })}</option>
            ))}
          </select>
        </Field>
        <div style={{ flex: 1 }} />
        <button type="button" className="ds-btn ds-btn-p" onClick={onExport} disabled={!data || exporting} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FileDown size={14} /> {exporting ? 'Génération…' : 'Exporter PDF'}
        </button>
      </div>

      {isLoading || !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
      ) : (
        <>
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">Synthèse — {data.periode.mois}</div>
                <div className="ds-bp-s">{data.filiale.fullName}</div>
              </div>
            </div>
            <div className="ds-bp-b" style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                <KV2 label="Conso flotte (mois)" value={data.consoMoyL100kmFlotte !== null ? `${data.consoMoyL100kmFlotte.toFixed(1)} L/100km` : '—'} strong />
                <KV2 label="Mois précédent"      value={data.consoMoyMoisPrecedent !== null ? `${data.consoMoyMoisPrecedent.toFixed(1)} L/100km` : '—'} />
                <KV2 label="Évolution"           value={data.evolutionPct !== null ? `${data.evolutionPct >= 0 ? '+' : ''}${data.evolutionPct.toFixed(1)}%` : '—'} color={data.evolutionPct !== null && data.evolutionPct > 0 ? '#dc2626' : '#16a34a'} strong />
                <KV2 label="Économies potentielles" value={`${fmtInt(data.economiesPotentielles.fcfa)} FCFA`} color="#16a34a" strong />
              </div>
            </div>
          </div>

          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h"><div><div className="ds-bp-t">Top 10 sur-consommateurs</div><div className="ds-bp-s">Camions avec ratio moyen &gt; 100% sur le mois</div></div></div>
            <div className="ds-bp-b">
              {data.topSurConsommateurs.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#16a34a', fontSize: 13 }}>✅ Aucun camion sur-consommateur</div>
              ) : (
                <table className="ds-tbl">
                  <thead><tr><th>Rang</th><th>Camion</th><th className="ds-am">Bons</th><th className="ds-am">Litres</th><th className="ds-am">Ratio moy.</th><th className="ds-am">Excès</th></tr></thead>
                  <tbody>
                    {data.topSurConsommateurs.map((c, i) => (
                      <tr key={c.camionCode}>
                        <td style={{ fontWeight: 700, color: i < 3 ? '#dc2626' : 'var(--tx-2)' }}>#{i + 1}</td>
                        <td><strong>{c.camionCode}</strong>{c.modele && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{c.modele}</div>}</td>
                        <td className="ds-am">{c.nbBons}</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(c.litres)}</td>
                        <td className="ds-am" style={{ fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{c.ratioMoyen.toFixed(0)}%</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums', color: '#dc2626' }}>+{fmtInt(c.sumEcartL)} L</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {data.recidivistes.length > 0 && (
            <div className="ds-bp" style={{ marginBottom: 14 }}>
              <div className="ds-bp-h"><div><div className="ds-bp-t" style={{ color: '#dc2626' }}>⚠️ Récidivistes</div><div className="ds-bp-s">Camions avec ≥1 anomalie marquée récidive ce mois</div></div></div>
              <div className="ds-bp-b">
                <table className="ds-tbl">
                  <thead><tr><th>Camion</th><th className="ds-am">Nb récidives</th></tr></thead>
                  <tbody>
                    {data.recidivistes.map((r) => (
                      <tr key={r.camionCode} style={{ background: 'rgba(239,68,68,0.06)' }}>
                        <td><strong>{r.camionCode}</strong></td>
                        <td className="ds-am" style={{ fontWeight: 700, color: '#dc2626' }}>{r.nbRecidives}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const KV2 = ({ label, value, color, strong }: { label: string; value: string; color?: string; strong?: boolean }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{
      fontSize: strong ? 18 : 14, fontWeight: strong ? 700 : 500,
      color: color ?? 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: 'normal',
    }}>{value}</div>
  </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
